package com.dotdrives.controller

import com.dotdrives.core.DotController
import com.dotdrives.model.NewReport
import com.dotdrives.model.ReportDefaultUpdate
import com.dotdrives.model.ReportStatus
import com.dotdrives.repository.ReportRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch

data class NewReportUiState(
    val title: String = "",
    val targetId: String? = null,
    val templateId: String? = null,
    val body: String = "",
    val saving: Boolean = false,
    val savedReportId: String? = null,
    val error: String? = null,
)

/** New-update composer (`design/portal-updates.dc.html`: "New giver update"). Both save paths
 *  always assign the new report to [assigneeUserId] -- matching the composer, which has no
 *  assignee field: a field rep files their own report, and this controller is never handed a
 *  different assignee to file on behalf of someone else. */
class NewReportController(
    private val repo: ReportRepository,
    scope: CoroutineScope,
    private val orgId: String,
    private val assigneeUserId: String,
) : DotController<NewReportUiState>(NewReportUiState(), scope) {
    fun start() {
        update { NewReportUiState() }
    }

    fun setTitle(text: String) {
        update { it.copy(title = text, error = null) }
    }

    fun setTargetId(id: String?) {
        update { it.copy(targetId = id, error = null) }
    }

    fun setTemplate(template: ReportDefaultUpdate?) {
        update {
            it.copy(
                templateId = template?.id,
                body = template?.message ?: it.body,
                error = null,
            )
        }
    }

    fun setBody(text: String) {
        update { it.copy(body = text, error = null) }
    }

    /** "Save draft" -- the report is created in its default `status = 'draft'`; the first
     *  update is posted only when there is a body to post. */
    fun saveDraft() {
        val current = state.value
        if (current.saving) return
        val title = current.title.trim()
        if (title.isEmpty()) {
            update { it.copy(error = "Give this update a title") }
            return
        }
        update { it.copy(saving = true, error = null, savedReportId = null) }
        scope.launch {
            repo.create(orgId, NewReport(title, current.targetId, assigneeUserId))
                .onSuccess { created ->
                    val body = current.body.trim()
                    if (body.isEmpty()) {
                        update { it.copy(saving = false, savedReportId = created.id) }
                    } else {
                        repo.postUpdate(orgId, created.id, body)
                            .onSuccess { update { it.copy(saving = false, savedReportId = created.id) } }
                            .onFailure { error ->
                                update { it.copy(saving = false, error = error.message ?: "Something went wrong") }
                            }
                    }
                }
                .onFailure { error -> update { it.copy(saving = false, error = error.message ?: "Something went wrong") } }
        }
    }

    /** "Submit for review" -- a report can't be submitted with an empty thread, so this
     *  requires both a title and a body (unlike [saveDraft], where a bodyless draft is valid). */
    fun submitForReview() {
        val current = state.value
        if (current.saving) return
        val title = current.title.trim()
        val body = current.body.trim()
        if (title.isEmpty()) {
            update { it.copy(error = "Give this update a title") }
            return
        }
        if (body.isEmpty()) {
            update { it.copy(error = "Write what happened before submitting") }
            return
        }
        update { it.copy(saving = true, error = null, savedReportId = null) }
        scope.launch {
            repo.create(orgId, NewReport(title, current.targetId, assigneeUserId))
                .onSuccess { created ->
                    repo.postUpdate(orgId, created.id, body)
                        .onSuccess {
                            repo.setStatus(orgId, created.id, ReportStatus.SUBMITTED)
                                .onSuccess { update { it.copy(saving = false, savedReportId = created.id) } }
                                .onFailure { error ->
                                    update { it.copy(saving = false, error = error.message ?: "Something went wrong") }
                                }
                        }
                        .onFailure { error ->
                            update { it.copy(saving = false, error = error.message ?: "Something went wrong") }
                        }
                }
                .onFailure { error -> update { it.copy(saving = false, error = error.message ?: "Something went wrong") } }
        }
    }
}
